import React from 'react';
import LegalLayout from './LegalLayout';

const H2 = ({ children }) => (
  <h2 className="font-serif text-ivory text-2xl md:text-3xl leading-tight mt-12 mb-4">
    {children}
  </h2>
);

const PrivacyPolicy = () => (
  <LegalLayout
    eyebrow="Privacy Policy"
    title="Your privacy, our standard."
    lastUpdated="May 12, 2026"
  >
    <p>
      Snatched Beauties Privé ("Snatched Beauties," "we," "us," or "our") respects your
      privacy and is committed to handling your personal information with the same care
      we bring to every treatment. This Privacy Policy explains what we collect, how we
      use it, and the rights you have over your data.
    </p>

    <H2>1. Information We Collect</H2>
    <p>When you interact with our website or book a service, we may collect:</p>
    <ul className="list-disc pl-6 space-y-2">
      <li>
        <strong className="text-ivory">Contact details</strong>: name, email address,
        phone number — provided voluntarily through our contact form, consultation
        booking, lead capture, or peptide program waitlist.
      </li>
      <li>
        <strong className="text-ivory">Marketing attribution</strong>: UTM parameters,
        referrer URL, and source tags — used to understand how clients discover us.
      </li>
      <li>
        <strong className="text-ivory">Payment information</strong>: processed directly
        by Stripe, Inc. We do not store credit card numbers on our servers.
      </li>
      <li>
        <strong className="text-ivory">Booking details</strong>: appointments scheduled
        through Calendly are processed by Calendly, LLC under their privacy policy.
      </li>
      <li>
        <strong className="text-ivory">Technical data</strong>: browser session
        information stored locally on your device (sessionStorage / localStorage) to
        improve your experience. We do not use third-party tracking cookies.
      </li>
    </ul>

    <H2>2. How We Use Your Information</H2>
    <ul className="list-disc pl-6 space-y-2">
      <li>To provide consultations, treatments, and program services you request.</li>
      <li>To process payments and deliver coupon codes.</li>
      <li>To communicate about your appointments, results, and follow-up care.</li>
      <li>To share studio updates, seasonal offerings, and program announcements
        (only if you opt in via our lead capture forms).</li>
      <li>To improve our website experience and understand our audience.</li>
    </ul>

    <H2>3. Third-Party Services</H2>
    <p>
      We use a select group of trusted partners to operate our business. Each adheres
      to its own privacy practices:
    </p>
    <ul className="list-disc pl-6 space-y-2">
      <li><strong className="text-ivory">Stripe</strong> — payment processing</li>
      <li><strong className="text-ivory">Calendly</strong> — appointment scheduling</li>
      <li><strong className="text-ivory">MongoDB Atlas / hosting provider</strong> —
        secure data storage</li>
    </ul>

    <H2>4. Your Rights</H2>
    <p>
      Depending on your location (including under GDPR for EU residents and CCPA for
      California residents), you have the right to:
    </p>
    <ul className="list-disc pl-6 space-y-2">
      <li>Request a copy of the personal information we hold about you.</li>
      <li>Request correction or deletion of your information.</li>
      <li>Opt out of marketing communications at any time.</li>
      <li>Request that we limit how we process your data.</li>
    </ul>
    <p>
      To exercise any of these rights, email{' '}
      <a href="mailto:support@snatchedbeauties.com" className="text-blush hover:underline">
        support@snatchedbeauties.com
      </a>
      . We will respond within 30 days.
    </p>

    <H2>5. Data Retention</H2>
    <p>
      We retain personal information only as long as necessary to provide services and
      meet legal obligations. Lead and contact records are kept for up to 36 months from
      last interaction, after which they are deleted or anonymized.
    </p>

    <H2>6. Security</H2>
    <p>
      We use industry-standard encryption (HTTPS) for all data in transit and follow
      best practices for data storage. While no system is perfectly secure, we work
      diligently to protect your information against unauthorized access.
    </p>

    <H2>7. Children's Privacy</H2>
    <p>
      Our services are intended for adults aged 18 and over. We do not knowingly
      collect personal information from minors.
    </p>

    <H2>8. Changes to This Policy</H2>
    <p>
      We may update this Privacy Policy from time to time. The "Last Updated" date at
      the top of this page reflects the most recent revision. Material changes will be
      communicated via the website or email where appropriate.
    </p>

    <H2>9. Contact</H2>
    <p>
      Snatched Beauties Privé<br />
      Los Angeles, California<br />
      Email:{' '}
      <a href="mailto:support@snatchedbeauties.com" className="text-blush hover:underline">
        support@snatchedbeauties.com
      </a>
      <br />
      Phone:{' '}
      <a href="tel:3236135153" className="text-blush hover:underline">323-613-5153</a>
    </p>
  </LegalLayout>
);

export default PrivacyPolicy;
